import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, Input, InputAmount, Modal } from '@/backoffice/components/ui';
import { LocalizationInputGroup, type LocalizationValue } from '@/backoffice/components/forms/LocalizationInputGroup';
import { formatCurrency, formatDate, formatTime } from '@/backoffice/utils';
import {
  fetchDriverTrips,
  createDriverTrip,
  deleteDriverTrip,
  type DriverTrip,
} from '@/domains/driver/services/driver.service';
import { Route, Plus, Calendar, Package, Trash2, Loader2, MapPin } from 'lucide-react';

const emptyLoc = (): LocalizationValue => ({
  countryId: null,
  regionId: null,
  prefectureId: null,
  communeId: null,
  districtId: null,
  secteurId: null,
});

const emptyForm = {
  departure_date: '',
  departure_time: '08:00',
  available_space: 0,
  price_per_kg: 500,
};

export function CommunityTripsPage() {
  const [trips, setTrips] = useState<DriverTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewTripForm, setShowNewTripForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [fromLoc, setFromLoc] = useState<LocalizationValue>(emptyLoc());
  const [toLoc, setToLoc] = useState<LocalizationValue>(emptyLoc());

  const load = () => {
    setLoading(true);
    fetchDriverTrips()
      .then(setTrips)
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setFromLoc(emptyLoc());
    setToLoc(emptyLoc());
    setError(null);
  };

  const handleCreate = async () => {
    if (!fromLoc.prefectureId || !fromLoc.communeId) {
      setError('Sélectionnez la ville et la commune de départ.');
      return;
    }
    if (!toLoc.prefectureId || !toLoc.communeId) {
      setError('Sélectionnez la ville et la commune d\'arrivée.');
      return;
    }
    if (!form.departure_date) {
      setError('Indiquez la date de départ.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const departureDate = new Date(`${form.departure_date}T${form.departure_time}`);
      await createDriverTrip({
        departure_prefecture_id: fromLoc.prefectureId,
        arrival_prefecture_id: toLoc.prefectureId,
        departure_commune_id: fromLoc.communeId,
        arrival_commune_id: toLoc.communeId,
        departure_date: departureDate.toISOString(),
        available_space: form.available_space,
        price_per_kg: form.price_per_kg,
      });
      resetForm();
      setShowNewTripForm(false);
      load();
    } catch {
      setError('Impossible de créer le trajet.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      await deleteDriverTrip(id);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="community_driver" title="Mes trajets">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="community_driver" title="Mes trajets">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <p className="text-sm text-gray-500">
          Planifiez vos trajets pour accepter des colis de vos voisins
        </p>
        <Button
          className="w-full sm:w-auto shrink-0"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => {
            resetForm();
            setShowNewTripForm(true);
          }}
        >
          Nouveau trajet
        </Button>
      </div>

      <Modal
        isOpen={showNewTripForm}
        onClose={() => setShowNewTripForm(false)}
        title="Nouveau trajet"
        size="full"
      >
        <div className="space-y-8 max-h-[min(75vh,720px)] overflow-y-auto overscroll-contain pr-1 -mr-1">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Départ puis Arrivée empilés : lisible sur mobile et desktop */}
          <div className="flex flex-col gap-6">
            <section className="rounded-2xl border border-orange-200/80 bg-orange-50/50 p-5 sm:p-6">
              <LocalizationInputGroup
                title="Départ"
                value={fromLoc}
                onChange={setFromLoc}
                maxLevel="commune"
                prefectureLabel="Ville"
                layout="stack"
              />
            </section>
            <section className="rounded-2xl border border-teal-200/80 bg-teal-50/50 p-5 sm:p-6">
              <LocalizationInputGroup
                title="Arrivée"
                value={toLoc}
                onChange={setToLoc}
                maxLevel="commune"
                prefectureLabel="Ville"
                layout="stack"
              />
            </section>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              label="Date de départ"
              type="date"
              value={form.departure_date}
              onChange={(e) => setForm((f) => ({ ...f, departure_date: e.target.value }))}
            />
            <Input
              label="Heure de départ"
              type="time"
              value={form.departure_time}
              onChange={(e) => setForm((f) => ({ ...f, departure_time: e.target.value }))}
            />
            <Input
              label="Espace disponible"
              type="number"
              min={0}
              step={1}
              value={form.available_space === 0 ? '' : String(form.available_space)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  available_space: Math.max(0, Number(e.target.value) || 0),
                }))
              }
              placeholder="Ex. 12"
              rightIcon={<span className="text-sm font-medium text-gray-500">kg</span>}
            />
            <InputAmount
              label="Prix par kg (GNF)"
              value={form.price_per_kg}
              onChange={(v) => setForm((f) => ({ ...f, price_per_kg: v }))}
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1 sticky bottom-0 bg-white pb-1">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setShowNewTripForm(false)}>
              Annuler
            </Button>
            <Button className="w-full sm:w-auto" isLoading={saving} onClick={handleCreate}>
              Créer le trajet
            </Button>
          </div>
        </div>
      </Modal>

      {trips.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Route className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucun trajet planifié</p>
          </div>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trips.map((trip) => (
            <Card key={trip.id}>
              <div className="flex items-center justify-between mb-4">
                <Badge variant={trip.status === 'planned' ? 'primary' : trip.status === 'in_progress' ? 'warning' : 'success'}>
                  {trip.status === 'planned' ? 'Planifié' : trip.status === 'in_progress' ? 'En cours' : 'Terminé'}
                </Badge>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-red-50"
                  onClick={() => handleDelete(trip.id)}
                  disabled={saving}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
              <div className="flex items-start gap-2 mb-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">De</p>
                  <p className="font-medium text-gray-900 truncate">{trip.from.city}</p>
                  {trip.from.commune && (
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {trip.from.commune}
                    </p>
                  )}
                </div>
                <Route className="w-5 h-5 text-blue-500 shrink-0 mt-4" />
                <div className="flex-1 min-w-0 text-right">
                  <p className="text-sm text-gray-500">À</p>
                  <p className="font-medium text-gray-900 truncate">{trip.to.city}</p>
                  {trip.to.commune && (
                    <p className="text-xs text-gray-500 mt-0.5 justify-end flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 shrink-0" />
                      {trip.to.commune}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(trip.departure_date)} à {formatTime(trip.departure_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Package className="w-4 h-4" />
                  <span>{trip.available_space} kg disponibles</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="font-bold text-emerald-600">{formatCurrency(trip.price_per_kg)}/kg</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
